import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Spinner,
  Textarea,
  Toaster,
  toast,
  useTimeFormatter,
} from "@itoresearch/design-system";
import {
  ArrowLeftIcon,
  CodeXmlIcon,
  CopyIcon,
  PencilIcon,
  Trash2Icon,
} from "lucide-react";

import { useUser } from "../hooks/use-user";
import {
  useApp,
  useAppGit,
  useCreateGitCredential,
  useDeleteApp,
  useProvisionGit,
  useRevokeGitCredential,
  useUpdateApp,
} from "../hooks/use-apps";
import { ApiError } from "../lib/api-client";
import type { AppSummary, GitCredentialReveal } from "../lib/apps";
import { copyText } from "../lib/clipboard";
import { CodeExplorer } from "../components/app-code";

export const Route = createFileRoute("/apps/$appId")({
  component: AppDetailPage,
});

function AppDetailPage() {
  const { user, isLoading } = useUser();
  const navigate = useNavigate();
  const { appId } = Route.useParams();
  const app = useApp(appId);
  const git = useAppGit(appId);
  const cloneUrl = git.data?.provisioned ? git.data.cloneUrl : undefined;
  const formatTime = useTimeFormatter();

  useEffect(() => {
    if (!isLoading && !user) navigate({ to: "/login" });
  }, [isLoading, user, navigate]);

  // A 404 after login means the app doesn't exist (or belongs to another
  // org) — offer a way back instead of an error.
  if (
    !isLoading &&
    user &&
    app.isError &&
    app.error instanceof ApiError &&
    app.error.status === 404
  ) {
    return (
      <div className="space-y-4">
        <Toaster />
        <Button render={<Link to="/apps" />} variant="ghost" size="sm">
          <ArrowLeftIcon data-slot="icon" /> All apps
        </Button>
        <Card>
          <CardHeader>
            <CardTitle>App not found</CardTitle>
            <CardDescription>
              This app does not exist or is not visible to your organization.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (!isLoading && user && app.isError) {
    return (
      <div className="space-y-4">
        <Toaster />
        <Button render={<Link to="/apps" />} variant="ghost" size="sm">
          <ArrowLeftIcon data-slot="icon" /> All apps
        </Button>
        <Card>
          <CardContent className="space-y-3 pt-6">
            <p className="text-sm">
              Failed to load app:{" "}
              {app.error instanceof Error
                ? app.error.message
                : "unexpected error"}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void app.refetch()}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (app.isPending || isLoading)
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Spinner /> Loading app…
      </div>
    );

  const data = app.data;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <Toaster />
      <Button render={<Link to="/apps" />} variant="ghost" size="sm">
        <ArrowLeftIcon data-slot="icon" /> All apps
      </Button>

      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">{data.name}</h1>
            <Badge variant={data.status === "ACTIVE" ? "default" : "secondary"}>
              {data.status.toLowerCase()}
            </Badge>
          </div>
          <p className="font-mono text-sm text-muted-foreground">{data.slug}</p>
          {data.description && (
            <p className="max-w-2xl text-sm text-muted-foreground">
              {data.description}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <EditAppDialog app={data} />
          <DeleteAppButton appId={data.appId} name={data.name} />
        </div>
      </div>

      {/* Code first — GitHub UX: the repository IS the app's primary view. */}
      {git.data?.provisioned ? (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <CodeXmlIcon className="size-4 text-muted-foreground" /> Code
              </CardTitle>
              {cloneUrl ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void copyText(cloneUrl, "Clone URL")}
                >
                  <CopyIcon data-slot="icon" /> Copy clone URL
                </Button>
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <CodeExplorer
              appId={data.appId}
              defaultBranch={git.data.defaultBranch}
            />
          </CardContent>
        </Card>
      ) : (
        <GitProvisionCard appId={data.appId} slug={data.slug} />
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {git.data?.provisioned ? (
          <GitRepositoryCard appId={data.appId} info={git.data} />
        ) : null}
        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <p className="text-xs uppercase text-muted-foreground">App ID</p>
              <p className="font-mono">{data.appId}</p>
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">
                Organization
              </p>
              <p className="font-mono">{data.organizationId}</p>
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">
                Created by
              </p>
              <p className="font-mono">{data.createdBy}</p>
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">Created</p>
              <p>{formatTime(data.createdAt)}</p>
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">Updated</p>
              <p>{formatTime(data.updatedAt)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Outlet />
    </div>
  );
}

function EditAppDialog({ app }: { app: AppSummary }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(app.name);
  const [description, setDescription] = useState(app.description ?? "");
  const update = useUpdateApp(app.appId);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    update.mutate(
      {
        name: name.trim(),
        description: description.trim() ? description.trim() : null,
      },
      {
        onSuccess: () => {
          setOpen(false);
          toast.add({ title: "App updated", type: "success" });
        },
        onError: (error) => {
          toast.add({
            title: "Could not update app",
            type: "error",
            description:
              error instanceof ApiError ? error.message : "Unexpected error.",
          });
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setName(app.name);
          setDescription(app.description ?? "");
        }
      }}
    >
      <DialogTrigger render={<Button variant="outline" size="sm" />}>
        <PencilIcon data-slot="icon" /> Edit
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Edit app</DialogTitle>
            <DialogDescription>
              The slug (“{app.slug}”) is immutable — it is the app's public
              address.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <Field>
              <FieldLabel htmlFor="edit-app-name">Name</FieldLabel>
              <Input
                id="edit-app-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={200}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="edit-app-description">
                Description
              </FieldLabel>
              <Textarea
                id="edit-app-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                maxLength={2000}
              />
              <FieldDescription>Leave empty to clear.</FieldDescription>
            </Field>
          </div>
          <DialogFooter>
            <DialogClose render={<Button variant="outline" />}>
              Cancel
            </DialogClose>
            <Button
              type="submit"
              disabled={update.isPending || name.trim().length === 0}
            >
              {update.isPending ? <Spinner /> : null} Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteAppButton({ appId, name }: { appId: string; name: string }) {
  const [confirming, setConfirming] = useState(false);
  const remove = useDeleteApp();
  const navigate = useNavigate();

  const remove_ = () => {
    remove.mutate(appId, {
      onSuccess: () => {
        toast.add({ title: "App deleted", type: "success", description: name });
        void navigate({ to: "/apps" });
      },
      onError: (error) => {
        toast.add({
          title: "Could not delete app",
          type: "error",
          description:
            error instanceof ApiError ? error.message : "Unexpected error.",
        });
      },
    });
  };

  return (
    <AlertDialog open={confirming} onOpenChange={setConfirming}>
      <AlertDialogTrigger render={<Button variant="outline" size="sm" />}>
        <Trash2Icon data-slot="icon" /> Delete
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete “{name}”?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently removes the app and everything attached to it. This
            action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={remove.isPending}
            onClick={remove_}
          >
            {remove.isPending ? <Spinner /> : null} Delete app
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ------------------------------------------------------------------
// Git storage (Harbor)
// ------------------------------------------------------------------

function GitProvisionCard({ appId, slug }: { appId: string; slug: string }) {
  const provision = useProvisionGit(appId);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Git repository</CardTitle>
        <CardDescription>
          Push code and tag releases on{" "}
          <span className="font-mono">git.itoresearch.com</span>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          The repository will live at{" "}
          <span className="font-mono text-foreground">apps/{slug}</span>.
        </p>
        <Button
          onClick={() =>
            provision.mutate(undefined, {
              onSuccess: () =>
                toast.add({ title: "Git repository created", type: "success" }),
              onError: (error) =>
                toast.add({
                  title: "Could not create the repository",
                  type: "error",
                  description:
                    error instanceof ApiError
                      ? error.message
                      : "Unexpected error.",
                }),
            })
          }
          disabled={provision.isPending}
        >
          {provision.isPending ? <Spinner /> : null} Create Git repository
        </Button>
      </CardContent>
    </Card>
  );
}

function GitRepositoryCard({
  appId,
  info,
}: {
  appId: string;
  info: NonNullable<ReturnType<typeof useAppGit>["data"]>;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Git repository</CardTitle>
        <CardDescription>
          Push code to{" "}
          <span className="font-mono">{info.harborRepositoryName}</span> on
          git.itoresearch.com.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div>
          <p className="text-xs uppercase text-muted-foreground">Clone URL</p>
          <div className="mt-1 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-md border px-3 py-2 font-mono text-xs">
              {info.cloneUrl}
            </code>
            <Button
              variant="outline"
              size="icon"
              onClick={() =>
                info.cloneUrl && void copyText(info.cloneUrl, "Clone URL")
              }
            >
              <CopyIcon data-slot="icon" />
            </Button>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            Default branch{" "}
            <span className="font-mono">{info.defaultBranch}</span> · generation{" "}
            {info.generation}
          </span>
          {info.hasActiveCredential ? (
            <span>An active Git credential exists.</span>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {info.hasActiveCredential ? (
            <RevokeCredentialButton appId={appId} />
          ) : null}
          <CreateCredentialButton appId={appId} />
        </div>
      </CardContent>
    </Card>
  );
}

function CreateCredentialButton({ appId }: { appId: string }) {
  const [reveal, setReveal] = useState<GitCredentialReveal | null>(null);
  const create = useCreateGitCredential(appId);

  const start = () => {
    create.mutate(undefined, {
      onSuccess: (result) => setReveal(result),
      onError: (error) =>
        toast.add({
          title: "Could not create the credential",
          type: "error",
          description:
            error instanceof ApiError ? error.message : "Unexpected error.",
        }),
    });
  };

  return (
    <>
      <Button size="sm" onClick={start} disabled={create.isPending}>
        {create.isPending ? <Spinner /> : null} Create Git credential
      </Button>
      <Dialog
        open={reveal !== null}
        onOpenChange={(open) => {
          if (!open) setReveal(null);
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Your Git credential</DialogTitle>
            <DialogDescription>
              Shown{" "}
              <span className="font-medium text-foreground">only once</span> —
              copy it now. If you lose it, revoke the credential and create a
              new one.
            </DialogDescription>
          </DialogHeader>
          {reveal && (
            <div className="grid gap-4 py-2">
              <Field>
                <FieldLabel htmlFor="cred-username">Username</FieldLabel>
                <div className="flex items-center gap-2">
                  <Input
                    id="cred-username"
                    readOnly
                    value={reveal.gitUsername}
                    className="font-mono text-xs"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Copy username"
                    onClick={() =>
                      void copyText(reveal.gitUsername, "Username")
                    }
                  >
                    <CopyIcon data-slot="icon" />
                  </Button>
                </div>
              </Field>
              <Field>
                <FieldLabel htmlFor="cred-password">
                  Password (API key)
                </FieldLabel>
                <div className="flex items-center gap-2">
                  <Input
                    id="cred-password"
                    readOnly
                    value={reveal.apiKey}
                    className="font-mono text-xs"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Copy API key"
                    onClick={() => void copyText(reveal.apiKey, "API key")}
                  >
                    <CopyIcon data-slot="icon" />
                  </Button>
                </div>
              </Field>
              <Field>
                <FieldLabel htmlFor="cred-clone">Clone</FieldLabel>
                <div className="flex items-center gap-2">
                  <Input
                    id="cred-clone"
                    readOnly
                    value={reveal.cloneCommand}
                    className="font-mono text-xs"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Copy clone command"
                    onClick={() =>
                      void copyText(reveal.cloneCommand, "Clone command")
                    }
                  >
                    <CopyIcon data-slot="icon" />
                  </Button>
                </div>
              </Field>
              <p className="text-xs text-muted-foreground">
                The API key is{" "}
                <span className="font-medium text-foreground">not stored</span>{" "}
                on our servers — if you lose it, revoke this credential and
                create a new one.
              </p>
            </div>
          )}
          <DialogFooter>
            <DialogClose render={<Button />}>Done</DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RevokeCredentialButton({ appId }: { appId: string }) {
  const [confirming, setConfirming] = useState(false);
  const revoke = useRevokeGitCredential(appId);

  // The active credential's id comes from GitInfo (fetched by the card).
  const git = useAppGit(appId);
  const credentialId = git.data?.activeCredentialId;

  const doRevoke = () => {
    if (!credentialId) return;
    revoke.mutate(credentialId, {
      onSuccess: () =>
        toast.add({ title: "Git credential revoked", type: "success" }),
      onError: (error) =>
        toast.add({
          title: "Could not revoke the credential",
          type: "error",
          description:
            error instanceof ApiError ? error.message : "Unexpected error.",
        }),
    });
  };

  return (
    <AlertDialog open={confirming} onOpenChange={setConfirming}>
      <AlertDialogTrigger render={<Button variant="outline" size="sm" />}>
        <Trash2Icon data-slot="icon" /> Revoke credential
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Revoke the Git credential?</AlertDialogTitle>
          <AlertDialogDescription>
            Pushes with this credential will fail immediately. Anyone still
            using it must create a new one.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={revoke.isPending || !credentialId}
            onClick={doRevoke}
          >
            {revoke.isPending ? <Spinner /> : null} Revoke
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
